import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type ResizeDirection = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

@Directive({
    selector: '[webResizeCornerHandle]',
    standalone: true,
})
export class ResizeCornerHandleDirective implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject(DOCUMENT);

    @Input() public targetElement: HTMLElement | null = null;
    @Input() public direction: ResizeDirection = 'bottom-right';
    @Input() public keepProportions = false;
    @Input() public rotation = 0;
    @Input() public minSize = 10;

    @Output() public sizeChangeStart = new EventEmitter<void>();
    @Output() public sizeChange = new EventEmitter<{ width: number; height: number }>();
    @Output() public sizeChangeEnd = new EventEmitter<void>();

    private startSub!: Subscription;
    private dragSub = new Subscription();

    private startX = 0;
    private startY = 0;
    private startWidth = 0;
    private startHeight = 0;
    private aspectRatio = 1;

    private rAF = 0;
    private pendingEvent: MouseEvent | null = null;

    public ngOnInit(): void {
        const mousedown$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown');
        const mousemove$ = fromEvent<MouseEvent>(this.document, 'mousemove');
        const mouseup$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        this.startSub = mousedown$.subscribe((startEvent) => {
            if (!(this.targetElement instanceof HTMLElement)) {
                console.warn('webResizeCornerHandle: targetElement is not set or invalid.');
                return;
            }

            startEvent.preventDefault();
            startEvent.stopPropagation();

            this.startX = startEvent.clientX;
            this.startY = startEvent.clientY;
            this.startWidth = this.targetElement.offsetWidth;
            this.startHeight = this.targetElement.offsetHeight;

            if (this.keepProportions && this.startWidth > 0 && this.startHeight > 0) {
                this.aspectRatio = this.startWidth / this.startHeight;
            }

            this.sizeChangeStart.emit();

            this.dragSub.unsubscribe();
            this.dragSub = new Subscription();

            this.dragSub.add(
                mousemove$.pipe(takeUntil(mouseup$)).subscribe((moveEvent) => {
                    this.pendingEvent = moveEvent;
                    if (this.rAF) return;
                    this.rAF = requestAnimationFrame(() => {
                        this.rAF = 0;
                        const ev = this.pendingEvent!;
                        this.pendingEvent = null;
                        this.handleMove(ev);
                    });
                })
            );

            this.dragSub.add(
                mouseup$.subscribe(() => {
                    this.cancelRaf();
                    this.dragSub.unsubscribe();
                    this.sizeChangeEnd.emit();
                })
            );
        });
    }

    private handleMove(event: MouseEvent): void {
        const rawDx = event.clientX - this.startX;
        const rawDy = event.clientY - this.startY;
        const theta = (this.rotation * Math.PI) / 180;
        const localDx = rawDx * Math.cos(theta) + rawDy * Math.sin(theta);
        const localDy = -rawDx * Math.sin(theta) + rawDy * Math.cos(theta);

        const { width, height } = this.calcSize(localDx, localDy);

        this.sizeChange.emit({
            width: Math.round(Math.max(this.minSize, width)),
            height: Math.round(Math.max(this.minSize, height)),
        });
    }

    private calcSize(localDx: number, localDy: number): { width: number; height: number } {
        const isRight = this.direction.includes('right');
        const isBottom = this.direction.includes('bottom');
        const dw = isRight ? localDx : -localDx;
        const dh = isBottom ? localDy : -localDy;

        if (!this.keepProportions) {
            return { width: this.startWidth + dw, height: this.startHeight + dh };
        }

        if (Math.abs(dw) > Math.abs(dh)) {
            const width = this.startWidth + dw;
            return { width, height: width / this.aspectRatio };
        }

        const height = this.startHeight + dh;
        return { width: height * this.aspectRatio, height };
    }

    private cancelRaf(): void {
        if (this.rAF) {
            cancelAnimationFrame(this.rAF);
            this.rAF = 0;
            this.pendingEvent = null;
        }
    }

    public ngOnDestroy(): void {
        this.cancelRaf();
        this.startSub?.unsubscribe();
        this.dragSub.unsubscribe();
    }
}
